import { DocSection, Callout, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';
import { Link } from 'react-router-dom';

export const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'how-it-works', label: 'How it works' },
  { id: 'named-unnamed', label: 'Named vs Unnamed people' },
  { id: 'face-collections', label: 'Face collections' },
  { id: 'splits', label: 'When one person splits into several groups' },
  { id: 'maintenance', label: 'Rebuild, ignore, delete' },
];

export default function Body() {
  return (
    <>
      <DocSection id="overview" title="Overview">
        <p>
          Name someone once and ClipRadar finds them in every clip you've analyzed — no tagging each
          appearance by hand. Face detection and naming are a standard part of ClipRadar, not a separate
          paid tier.
        </p>
        <DocShot slug="people-and-faces" shot="board" alt="People board with Named and Unnamed sections" caption="The People board: Named People you've identified, Unnamed People waiting to be reviewed." />
      </DocSection>
      <DocSection id="how-it-works" title="How it works">
        <p>
          Run the Faces pass on a source and ClipRadar detects every face in it, then automatically
          groups similar-looking faces into <strong>clusters</strong> — matching the same person across
          different clips without any input from you. Type a name on a cluster (or fold it into an
          existing person) and it becomes a <strong>person</strong> you can filter searches by, across
          your whole library.
        </p>
        <p>
          For a step-by-step walkthrough of naming and cleaning up a library's first pass, see{' '}
          <Link to="/docs/guides/find-and-name-people">Find &amp; name people</Link>.
        </p>
      </DocSection>
      <DocSection id="named-unnamed" title="Named vs Unnamed people">
        <p>
          The board has two sections. <span style={kbd}>Named People</span> is a dense grid of people
          you've identified — search can find them by name or through the People filter on the Search
          tab. <span style={kbd}>Unnamed People</span> holds the raw clusters ClipRadar grouped
          automatically, shown larger for verification: click to select, shift-click for a range,
          right-click for merge and more. Typing a name on a cluster turns it into a person.
        </p>
      </DocSection>
      <DocSection id="face-collections" title="Face collections">
        <p>
          Group related people into a <strong>Face Collection</strong> — a cast list, a recurring
          interviewee set, anything you'd want to filter by as a unit — from a person card's{' '}
          <span style={kbd}>Add to category…</span> menu. Collections live in their own sidebar next to
          the People board.
        </p>
      </DocSection>
      <DocSection id="splits" title="When one person splits into several groups">
        <p>
          Automatic clustering isn't perfect — a different angle, lighting, or expression can look
          different enough to the model that the same person ends up split across more than one Unnamed
          group.
        </p>
        <p>
          There are two ways to fix it. On an Unnamed cluster, <span style={kbd}>Add to person…</span> folds
          it straight into an existing name. On an already-named person,{' '}
          <span style={kbd}>Review matching faces</span> searches the Unnamed groups for likely matches
          and surfaces them in a review banner for you to confirm or reject — nothing merges silently.
        </p>
        <DocShot slug="people-and-faces" shot="review-menu" alt="A person card menu showing Review matching faces" caption="Review matching faces looks for split groups that belong to this person." />
        <Callout kind="tip">
          Splitting is far more common than a wrong merge — if a name feels incomplete, run{' '}
          <span style={kbd}>Review matching faces</span> on it before assuming ClipRadar missed the
          person entirely.
        </Callout>
      </DocSection>
      <DocSection id="maintenance" title="Rebuild, ignore, delete">
        <p>
          Deleting a cluster just dismisses it from Unnamed People — the underlying faces aren't
          searched again until you run <span style={kbd}>Rebuild Groups</span>, which reclusters every
          detected face from scratch. Named people are kept as-is, and previously deleted clusters can
          resurface.
        </p>
        <p>
          <span style={kbd}>Ignore permanently…</span> blacklists specific faces so they never resurface
          at all — useful for extras or false detections; <span style={kbd}>Reset ignored faces</span> undoes
          that if you change your mind. <span style={kbd}>Delete Unnamed</span> (type-to-confirm) clears
          every Unnamed group at once, for when you want to start review from a clean board.
        </p>
      </DocSection>
    </>
  );
}
